import { useMemo, useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AccountMembershipsDocument,
  CreateAccountMembershipDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface AddAccountMembershipModalProps {
  onClose: () => void
  accountId: number
}

export const AddAccountMembershipModal = ({
  onClose,
  accountId,
}: AddAccountMembershipModalProps) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined)

  const { data: membershipsData } = useQuery(AccountMembershipsDocument, {
    variables: { accountIds: [accountId] },
  })

  const existingMemberEmployeeIds = useMemo(
    () => membershipsData?.banking.accountMemberships.map((m) => m.employee.id) ?? [],
    [membershipsData]
  )

  const [createMembership, { loading }] = useMutation(CreateAccountMembershipDocument, {
    refetchQueries: [AccountMembershipsDocument],
    extractorFn: (data) => data.bankingMutations.createBankingAccountMembership,
  })

  const handleSubmit = async () => {
    if (!selectedEmployeeId) return

    const result = await createMembership({
      variables: {
        accountId,
        employeeId: selectedEmployeeId,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      const membership = handled.data?.accountMembership

      // Check if OAuth authorization is needed first
      if (membership?.authorizationUrl) {
        window.location.href = membership.authorizationUrl
        return
      }

      // Then check for consent URL (Swan SCA)
      const consentUrl = membership?.consent?.consentUrl
      if (consentUrl) {
        window.location.href = consentUrl
      } else {
        onClose()
      }
    } else {
      const errorMessage =
        handled.errors
          ?.map((e) => ('messages' in e ? e.messages.join(' ') : e.message))
          .join(' ') || i18n.t('errors.generic_error')
      app.setFlash({
        type: 'wrong',
        message: errorMessage,
      })
    }
  }

  return (
    <Modal
      title={i18n.t('banking.account_memberships.add_modal.title')}
      description={i18n.t('banking.account_memberships.add_modal.description')}
      illustration={'add-user'}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedEmployeeId || loading}>
            {i18n.t('banking.account_memberships.actions.add')}
          </Button>
        </Box>
      }
    >
      <EmployeeSelectorV2
        value={selectedEmployeeId}
        onChange={setSelectedEmployeeId}
        forcedFilters={{
          excludeIds: existingMemberEmployeeIds,
        }}
      />
    </Modal>
  )
}
